import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import ConfirmModal from '../ui/ConfirmModal';
import { showingService } from '../../_services/showing.service';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const STATUS_LABEL = {
  open: 'Open', accepted: 'Accepted', confirmed: 'Confirmed', in_progress: 'In progress',
  completed: 'Completed', reported: 'Report ready', paid: 'Paid', cancelled: 'Cancelled', expired: 'Expired'
};

// tab → statuses it includes
const TABS = [
  { key: 'all', label: 'All', statuses: null },
  { key: 'open', label: 'Unassigned', statuses: ['open'] },
  { key: 'accepted', label: 'Accepted', statuses: ['accepted', 'confirmed', 'in_progress'] },
  { key: 'completed', label: 'Completed', statuses: ['completed', 'reported', 'paid'] },
  { key: 'cancelled', label: 'Canceled', statuses: ['cancelled'] },
  { key: 'expired', label: 'Expired', statuses: ['expired'] }
];

const INTEREST_LABEL = {
  very_interested: 'Very interested', interested: 'Interested',
  neutral: 'Neutral', not_interested: 'Not interested'
};

const CANCELLED_BY_LABEL = {
  buyer_agent: "the buyer's agent", showing_agent: 'the showing agent',
  admin: 'an admin', system: 'the system'
};

// the life of a showing, as a progress track
const STEPS = ['Posted', 'Accepted', 'Confirmed', 'Showing', 'Done'];
const STEP_INDEX = { open: 0, accepted: 1, confirmed: 2, in_progress: 3, completed: 4, reported: 4, paid: 4 };
const FINAL_LABEL = { completed: 'Done', reported: 'Reported', paid: 'Paid' };

function fmtTime(d) {
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function dayParts(d) {
  const dt = new Date(d);
  return {
    dow: dt.toLocaleDateString('en-US', { weekday: 'short' }),
    day: dt.getDate(),
    mon: dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  };
}

function Stepper({ status }) {
  if (status === 'cancelled' || status === 'expired') return null;
  const at = STEP_INDEX[status] != null ? STEP_INDEX[status] : 0;
  return (
    <div className="st-track">
      {STEPS.map((lbl, i) => (
        <div key={lbl} className={'st-step' + (i < at ? ' done' : i === at ? ' now' : '')}>
          <span className="dot" />
          <span className="lbl">{i === 4 && FINAL_LABEL[status] ? FINAL_LABEL[status] : lbl}</span>
        </div>
      ))}
    </div>
  );
}

function ReviewForm({ id, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  return (
    <div className="sh-report-box">
      <b>Rate this showing</b>
      <div className="rv-form-stars">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" className={n <= rating ? 'on' : ''} onClick={() => setRating(n)}>★</button>
        ))}
      </div>
      <textarea rows="2" style={{ width: '100%', boxSizing: 'border-box', marginTop: 6 }}
        placeholder="A sentence about how it went (optional)"
        value={comment} onChange={e => setComment(e.target.value)} />
      {error ? <div className="sh-error" style={{ marginTop: 8 }}>{error}</div> : null}
      <div className="sh-actions" style={{ marginTop: 8 }}>
        <button className="sh-btn sh-btn-primary" disabled={busy} onClick={() => {
          setBusy(true);
          setError(null);
          showingService.review(id, rating, comment).then(res => {
            setBusy(false);
            if (res.status === 'success') onDone();
            else setError(res.message || 'Could not submit the review.');
          }).catch(() => { setBusy(false); setError('Network error — try again.'); });
        }}>{busy ? 'Sending…' : 'Submit review'}</button>
        <button className="sh-btn sh-btn-outline" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

function ReportForm({ id, onDone }) {
  const [interest, setInterest] = useState('interested');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  return (
    <div className="sh-report-box">
      <b>File the showing report</b>
      <select value={interest} onChange={e => setInterest(e.target.value)} style={{ marginTop: 4 }}>
        {Object.keys(INTEREST_LABEL).map(k => <option key={k} value={k}>{INTEREST_LABEL[k]}</option>)}
      </select>
      <textarea rows="3" style={{ width: '100%', boxSizing: 'border-box', marginTop: 6 }}
        placeholder="How did it go? What did the buyer react to?"
        value={notes} onChange={e => setNotes(e.target.value)} />
      {error ? <div className="sh-error" style={{ marginTop: 8 }}>{error}</div> : null}
      <div className="sh-actions" style={{ marginTop: 8 }}>
        <button className="sh-btn sh-btn-primary" disabled={busy} onClick={() => {
          setBusy(true);
          setError(null);
          showingService.fileReport(id, { interest_level: interest, notes }).then(res => {
            setBusy(false);
            if (res.status === 'success') onDone();
            else setError(res.message || 'Could not send the report.');
          }).catch(() => { setBusy(false); setError('Network error — try again.'); });
        }}>{busy ? 'Sending…' : 'Send report'}</button>
        <button className="sh-btn sh-btn-outline" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

export default function ShowingsTable({ as, title, subtitle, location }) {
  const isAgentView = as === 'showing_agent';
  const params = new URLSearchParams(location ? location.search : '');
  const [tab, setTab] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [state, setState] = useState('');
  const [q, setQ] = useState('');
  const [dir, setDir] = useState('asc'); // agenda reads soonest-first by default
  const [data, setData] = useState(null);
  const [justPosted] = useState(params.get('posted') === '1');
  const [inlineForm, setInlineForm] = useState(null); // { id, kind: 'report'|'review' }
  const [confirming, setConfirming] = useState(null); // { id, kind: 'cancel'|'withdraw', status }
  const [actError, setActError] = useState(null); // transient banner when a status change fails

  const load = useCallback(() => {
    const t = TABS.find(x => x.key === tab);
    showingService.mine({
      as,
      status: t && t.statuses ? t.statuses.join(',') : null,
      from, to, state, q, sort: 'window_start', dir
    }).then(res => {
      if (res.status === 'success') setData(res);
      else setActError(res.message || 'Could not load showings.');
    }).catch(() => { setActError('Could not load showings.'); });
  }, [as, tab, from, to, state, q, dir]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const counts = (data && data.counts) || {};
  const tabCount = t => t.statuses
    ? t.statuses.reduce((n, s) => n + (counts[s] || 0), 0)
    : Object.values(counts).reduce((a, b) => a + b, 0);

  const act = (id, action, reason) => {
    setActError(null);
    return showingService.setStatus(id, action, reason).then(res => {
      if (res.status !== 'success') setActError(res.message || 'That action failed — the showing may have changed. The list has been refreshed.');
      load();
    }).catch(() => { setActError('Network error — try again.'); load(); });
  };

  const clearFilters = () => { setFrom(''); setTo(''); setState(''); setQ(''); };

  const list = data ? data.showings : null;

  // group by calendar day, preserving server order
  let groups = [];
  if (list) {
    let cur = null;
    list.forEach(s => {
      const key = new Date(s.window_start).toDateString();
      if (!cur || cur.key !== key) {
        cur = { key, date: s.window_start, items: [] };
        groups.push(cur);
      }
      cur.items.push(s);
    });
  }
  const todayKey = new Date().toDateString();

  // "Soonest first" reads as an agenda: today & upcoming on top,
  // then past days (most recent first) under an "Earlier" divider.
  let pastStart = -1;
  if (dir === 'asc' && groups.length) {
    const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
    const upcoming = groups.filter(g => new Date(g.date) >= midnight);
    const past = groups.filter(g => new Date(g.date) < midnight).reverse();
    if (upcoming.length && past.length) pastStart = upcoming.length;
    groups = upcoming.concat(past);
  }

  return (
    <DashboardLayout>
      <h1>{title}</h1>
      {subtitle ? <p className="sh-page-sub">{subtitle}</p> : null}
      {justPosted ? <div className="sh-success" style={{ marginBottom: 16 }}>Showing posted! Nearby verified agents have been alerted.</div> : null}
      {actError ? (
        <div className="sh-error" style={{ marginBottom: 16, cursor: 'pointer' }} onClick={() => setActError(null)} title="Dismiss">
          {actError}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div className="sh-tabs">
          {TABS.map(t => (
            <button key={t.key} className={'sh-tab' + (tab === t.key ? ' active' : '')} onClick={() => setTab(t.key)}>
              {t.label}<span className="ct">{tabCount(t)}</span>
            </button>
          ))}
        </div>
        <button className="sh-btn sh-btn-outline" style={{ minHeight: 38 }} onClick={() => setDir(d => (d === 'asc' ? 'desc' : 'asc'))}>
          {dir === 'asc' ? '↑ Soonest first' : '↓ Latest first'}
        </button>
      </div>

      <div className="db-filters">
        <div className="sh-field">
          <label>Start date</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="sh-field">
          <label>End date</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="sh-field">
          <label>State</label>
          <select value={state} onChange={e => setState(e.target.value)}>
            <option value="">All states</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sh-field grow">
          <label>Search</label>
          <input placeholder="Search by address, city, buyer, agent, or showing ID"
            value={q} onChange={e => setQ(e.target.value)} />
        </div>
        {(from || to || state || q) ? <button className="clear-link" onClick={clearFilters}>Clear search</button> : null}
      </div>

      {list === null ? (
        <div className="wd-empty"><b>Loading your showings…</b></div>
      ) : list.length === 0 ? (
        <div className="wd-empty">
          <div className="glyph">🗓</div>
          <b>Nothing here yet</b>
          <p>
            There are no showings with the selected filters.
            {!isAgentView ? <> Ready when you are — <Link to="/showings/post">post a showing</Link> and nearby agents will be alerted.</> : null}
          </p>
        </div>
      ) : (
        <div className="ag-groups">
          {groups.map((g, gi) => {
            const dp = dayParts(g.date);
            const isToday = g.key === todayKey;
            return (
              <React.Fragment key={g.key}>
              {gi === pastStart ? <div className="ag-divider"><span>Earlier</span></div> : null}
              <div className="ag-group">
                <div className={'ag-date' + (isToday ? ' today' : '')}>
                  <div className="dow">{isToday ? 'Today' : dp.dow}</div>
                  <div className="day">{dp.day}</div>
                  <div className="mon">{dp.mon}</div>
                </div>
                <div className="ag-cards">
                  {g.items.map(s => {
                    const p = s.properties[0] || {};
                    const other = isAgentView ? s.buyer_agent : s.showing_agent;
                    const canReview = ['completed', 'reported', 'paid'].indexOf(s.status) !== -1 && !s.my_review_done && (isAgentView || s.showing_agent);
                    const dead = s.status === 'cancelled' || s.status === 'expired';
                    const done = ['completed', 'reported', 'paid'].indexOf(s.status) !== -1;
                    return (
                      <div className={'ag-card' + (done ? ' is-done' : '') + (dead ? ' is-dead' : '')} key={s.request_id}>
                        <div className="ag-row">
                          <div className="ag-when">
                            <div className="t">{fmtTime(s.window_start)}</div>
                            <div className="w">until {fmtTime(s.window_end)}<br />{s.duration_minutes} min</div>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="ag-title">{p.address || 'Showing'}</div>
                            <div className="ag-sub">
                              {[p.city, p.state, p.zip].filter(Boolean).join(', ')}
                              {s.properties.length > 1 ? ` · +${s.properties.length - 1} more stop${s.properties.length > 2 ? 's' : ''}` : ''}
                            </div>
                            <div className="ag-people">
                              {s.buyer_name ? <span className="who"><span className="lbl">Buyer</span> {s.buyer_name}</span> : null}
                              {other && other.name ? (
                                <span className="who">
                                  <span className="lbl">{isAgentView ? 'Requested by' : 'Covered by'}</span> {other.name}
                                  {other.phone_number ? <a href={'tel:' + other.phone_number}>{other.phone_number}</a> : null}
                                </span>
                              ) : (!isAgentView && s.status === 'open' ? <span className="who"><span className="lbl">Covered by</span> waiting for an agent…</span> : null)}
                            </div>
                            {dead ? (
                              <div style={{ marginTop: 12 }}>
                                <span className={'sh-status ' + s.status}>{STATUS_LABEL[s.status] || s.status}</span>
                                {s.status === 'cancelled' && s.cancelled ? (
                                  <div className="sh-access-note" style={{ background: '#fdf1ee', marginTop: 8 }}>
                                    ✕ Cancelled by <b>{CANCELLED_BY_LABEL[s.cancelled.by] || 'someone'}</b>
                                    {s.cancelled.reason ? `: "${s.cancelled.reason}"` : ''}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <Stepper status={s.status} />
                            )}
                          </div>
                          <div className="ag-side">
                            <span className="ag-fee">${s.fee}</span>
                            <span className="ag-id">#{s.request_id.slice(0, 8)}</span>
                            <div className="ag-actions">
                              {!isAgentView && s.status === 'accepted' ? (
                                <button className="sh-btn sh-btn-primary" onClick={() => act(s.request_id, 'confirm')}>Confirm agent</button>
                              ) : null}
                              {isAgentView && ['accepted', 'confirmed'].indexOf(s.status) !== -1 ? (
                                <button className="sh-btn sh-btn-primary" onClick={() => act(s.request_id, 'start')}>Start showing</button>
                              ) : null}
                              {isAgentView && s.status === 'in_progress' ? (
                                <button className="sh-btn sh-btn-green" onClick={() => act(s.request_id, 'complete')}>Mark complete</button>
                              ) : null}
                              {isAgentView && s.status === 'completed' ? (
                                <button className="sh-btn sh-btn-primary" onClick={() => setInlineForm({ id: s.request_id, kind: 'report' })}>File report</button>
                              ) : null}
                              {canReview ? (
                                <button className="sh-btn sh-btn-outline" onClick={() => setInlineForm({ id: s.request_id, kind: 'review' })}>Leave review</button>
                              ) : null}
                              {['open', 'accepted', 'confirmed', 'in_progress'].indexOf(s.status) !== -1 ? (
                                !isAgentView ? (
                                  <button className="sh-btn sh-btn-danger-ghost" onClick={() => setConfirming({ id: s.request_id, kind: 'cancel', status: s.status })}>Cancel</button>
                                ) : s.status !== 'open' ? (
                                  <button className="sh-btn sh-btn-danger-ghost" onClick={() => setConfirming({ id: s.request_id, kind: 'withdraw', status: s.status })}>Withdraw</button>
                                ) : null
                              ) : null}
                            </div>
                          </div>
                        </div>

                        {(s.report && s.report.submitted_at) || (isAgentView && p.access_notes) || (!isAgentView && s.withdrawals && s.withdrawals.length > 0) || (inlineForm && inlineForm.id === s.request_id) ? (
                          <div className="ag-attach">
                            {isAgentView && p.access_notes ? <div className="sh-access-note">🔑 {p.access_notes}</div> : null}
                            {!isAgentView && s.withdrawals && s.withdrawals.length > 0 ? (
                              <div className="sh-access-note" style={{ background: '#fdf1ee' }}>
                                {s.withdrawals.map((w, i) => (
                                  <div key={i}>↩ <b>{w.name || 'An agent'}</b> withdrew{w.reason ? `: "${w.reason}"` : ''}</div>
                                ))}
                              </div>
                            ) : null}
                            {s.report && s.report.submitted_at ? (
                              <div className="sh-report-box">
                                <b>Showing report — {INTEREST_LABEL[s.report.interest_level] || '—'}</b>
                                {s.report.notes}
                              </div>
                            ) : null}
                            {inlineForm && inlineForm.id === s.request_id && inlineForm.kind === 'report' ? (
                              <ReportForm id={s.request_id} onDone={() => { setInlineForm(null); load(); }} />
                            ) : null}
                            {inlineForm && inlineForm.id === s.request_id && inlineForm.kind === 'review' ? (
                              <ReviewForm id={s.request_id} onDone={() => { setInlineForm(null); load(); }} />
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {confirming ? (
        confirming.kind === 'withdraw' ? (
          <ConfirmModal
            title="Withdraw from this showing?"
            message="The showing reopens for other agents, and the requesting agent is notified with your reason."
            confirmLabel="Withdraw"
            danger
            withReason
            reasonRequired
            reasonLabel="Why are you withdrawing?"
            reasonPlaceholder="e.g. A closing ran long and I can't make the window."
            onConfirm={reason => { act(confirming.id, 'cancel', reason); setConfirming(null); }}
            onClose={() => setConfirming(null)}
          />
        ) : (
          <ConfirmModal
            title="Cancel this showing?"
            message="If an agent already accepted, they'll be notified."
            confirmLabel="Cancel showing"
            danger
            withReason
            reasonRequired={confirming.status !== 'open'}
            reasonLabel="Reason"
            reasonPlaceholder="e.g. Buyer went under contract on another home."
            onConfirm={reason => { act(confirming.id, 'cancel', reason); setConfirming(null); }}
            onClose={() => setConfirming(null)}
          />
        )
      ) : null}
    </DashboardLayout>
  );
}
